import { useMemo, useState } from 'react';
import type { WorkLog } from '@aegislab/shared';
import { Button, Input } from '@/shared/ui/atoms';
import { DataLoadError } from '@/shared/ui/DataLoadError';
import { formatMonthDayKo } from '@/shared/lib/format';
import { historyEntries } from './historyEntries';
import { MissingDayForm } from './MissingDayForm';
import { WorklogHistoryDay } from './WorklogHistoryDay';
import * as s from './WorklogHistoryReader.css';

export interface WorklogHistoryReaderProps {
  /** 지금까지 불러온 내 일지(순서 무관). */
  readonly logs: readonly WorkLog[];
  /** 오늘 일지 날짜 — 이 날짜와 그 뒤 일지는 지난 일지에서 빼고 오늘 메모에서 고친다. */
  readonly todayDate: string;
  readonly hasMore: boolean;
  readonly isLoadingMore?: boolean;
  readonly onLoadMore: () => void;
  /** 있는 일지의 본문을 저장한다. */
  readonly onUpdateMemo: (workLogId: string, content: string) => Promise<unknown>;
  /** 일지가 없던 날에 새 일지를 만든다. */
  readonly onCreateLog: (logDate: string, content: string) => Promise<unknown>;
  readonly isError?: boolean;
  readonly onRetry?: () => void;
}

/** 처음 펼치는 날 수이자 「이전 일지 더 읽기」 한 번에 더 펼치는 날 수. */
const DAYS_PER_STEP = 5;

/**
 * 지난 일지 — 최근 기록의 메모와 태스크부터 읽고, 지난 메모를 고친다.
 * 검색과 기간은 필요할 때 좁히는 보조 수단이다(gbrain decisions/today-worklog-reading-first-2026-10-01).
 */
export function WorklogHistoryReader({
  logs,
  todayDate,
  hasMore,
  isLoadingMore = false,
  onLoadMore,
  onUpdateMemo,
  onCreateLog,
  isError = false,
  onRetry,
}: WorklogHistoryReaderProps) {
  const [query, setQuery] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  const [visibleDays, setVisibleDays] = useState(DAYS_PER_STEP);
  const invalidRange = !!(range.from && range.to && range.from > range.to);
  const narrowed = !!(query.trim() || range.from || range.to);
  const entries = useMemo(
    () => (invalidRange ? [] : historyEntries(logs, todayDate, { query, ...range })),
    [logs, todayDate, query, range, invalidRange]
  );
  const loggedDates = useMemo(() => new Set(logs.map((log) => log.logDate)), [logs]);
  const shown = narrowed ? entries : entries.slice(0, visibleDays);
  const moreLoaded = entries.length > visibleDays;

  function readMore() {
    const next = visibleDays + DAYS_PER_STEP;
    setVisibleDays(next);
    // 불러온 것을 다 펼치게 되면 다음 묶음을 미리 받는다 — 도착하면 같은 자리에 이어 붙는다.
    if (next >= entries.length && hasMore) onLoadMore();
  }

  function reset() {
    setQuery('');
    setRange({ from: '', to: '' });
    setVisibleDays(DAYS_PER_STEP);
  }

  if (isError) return <DataLoadError subject="지난 일지" onRetry={onRetry} />;

  return (
    <div className={s.reader}>
      <div className={s.tools}>
        <Input
          type="search"
          label="일지 내용 검색"
          placeholder="기억나는 업무나 메모로 찾기"
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
        />
        <details className={s.period}>
          <summary className={s.periodSummary}>날짜로 좁히기</summary>
          <div className={s.range}>
            <Input
              type="date"
              label="시작일"
              fullWidth
              value={range.from}
              onChange={(event) => {
                const from = event.currentTarget.value;
                setRange((current) => ({ ...current, from }));
              }}
            />
            <Input
              type="date"
              label="종료일"
              fullWidth
              value={range.to}
              onChange={(event) => {
                const to = event.currentTarget.value;
                setRange((current) => ({ ...current, to }));
              }}
            />
          </div>
          {invalidRange && (
            <p className={s.small} role="alert">
              종료일을 시작일 이후로 선택해 주세요.
            </p>
          )}
        </details>
        <div className={s.toolbar}>
          <p className={s.small} role="status">
            {narrowed ? `${entries.length}일의 기록을 찾았습니다.` : '최근 일지부터 읽고 있습니다.'}
            {range.from &&
              range.to &&
              ` ${formatMonthDayKo(range.from)} ~ ${formatMonthDayKo(range.to)}`}
          </p>
          {narrowed && (
            <Button size="xs" variant="link" onClick={reset}>
              최근 일지로 돌아가기
            </Button>
          )}
          <MissingDayForm
            todayDate={todayDate}
            loggedDates={loggedDates}
            onCreateLog={onCreateLog}
          />
        </div>
      </div>

      {shown.map((log) => (
        <WorklogHistoryDay key={log.id} log={log} query={query} onUpdateMemo={onUpdateMemo} />
      ))}

      {!shown.length && (
        <div className={s.context}>
          <p className={s.muted}>
            {narrowed ? '이 조건에 맞는 일지가 없습니다.' : '아직 지난 일지가 없습니다.'}
          </p>
          {narrowed && (
            <Button size="xs" variant="link" onClick={reset}>
              최근 일지 읽기
            </Button>
          )}
        </div>
      )}

      {!narrowed && (moreLoaded || hasMore) && (
        <Button variant="outline" size="sm" onClick={readMore} disabled={isLoadingMore}>
          이전 일지 더 읽기
        </Button>
      )}
    </div>
  );
}
